import { createPortal } from 'react-dom';

type SvgTooltipProps = {
  /** Ref to the tooltip DOM element, managed by `useSvgTooltip` */
  ref: React.Ref<HTMLDivElement>;
  /** Content to display in the tooltip */
  children: React.ReactNode;
  /** Whether the tooltip is visible */
  visible: boolean;
};

/**
 * Lightweight tooltip rendered as a portal, designed for SVG canvas overlays.
 *
 * **Visibility and content** are controlled via React props (re-renders only when
 * the hovered item changes). **Position** is updated imperatively via the ref
 * managed by `useSvgTooltip` to avoid re-renders on every pointer move.
 *
 * @example
 * ```tsx
 * const { tooltipRef, updatePosition } = useSvgTooltip();
 *
 * // In a pointermove handler (imperative — no setState):
 * updatePosition(event.clientX, event.clientY);
 *
 * <SvgTooltip ref={tooltipRef} visible={!!hoveredItem}>
 *   ...tooltip content...
 * </SvgTooltip>
 * ```
 */
const SvgTooltip = ({ ref, children, visible }: SvgTooltipProps) =>
  typeof document === 'undefined'
    ? null
    : createPortal(
        <div
          ref={ref}
          role="tooltip"
          aria-hidden={!visible}
          className="pointer-events-none fixed z-50 max-w-75 rounded-md bg-popover shadow-md transition-opacity duration-100"
          style={{
            left: 0,
            top: 0,
            opacity: visible ? 1 : 0,
            visibility: visible ? 'visible' : 'hidden',
          }}
        >
          {children}
        </div>,
        document.body,
      );

export { SvgTooltip, type SvgTooltipProps };
